<template>
    <div class="modal-container">
        <div class="modal-header">
            <h4 class="modal-title">Create Shipment</h4>
            <md-button class="md-simple md-just-icon md-round modal-default-button" @click.stop="$modal.hide">
                <md-icon>clear</md-icon>
            </md-button>
        </div>
        <div>
            <md-button class="md-success chargeButton-toggle" @click="openCharges()">
                Charges ({{ chargeList.length }})
            </md-button>
            <charge-list-modal
                :rates-list="chargeList"
                :rates-is-loading="ratesIsLoading"
                :rate-list-data="rateListData"
                :currency="currency"
                :rule-data="chargeRule"
            />
        </div>

        <div class="modal"></div>
        <div class="modal-body shipment-form-modal">
            <form-wrapper :validator="$v.shipmentDetails" class="form-wrapper">
                <tabs
                    :tab-name="['Shipment', 'Pickup Options', 'Drop off Options']"
                    color-button="warning"
                    class="custom-tab-list"
                    @switch-panel="handleSwitchTab"
                >
                    <template slot="tab-pane-1">
                        <div class="shipment-form-container">
                            <charging-type-options
                                @selectedOption="handleRateTypeChanged"
                                :selected-charge-type-id="shipmentDetails.rateGroupId"
                            />

                            <team-region-member-controls
                                :team-region-id="shipmentDetails.teamRegionId"
                                :public-user-id="shipmentDetails.assignToPublicUserId"
                                :carrier-team-id="shipmentDetails.carrierTeamId"
                                @onChanged="handleTeamRegionMemberChanged"
                                :strict-team-region-filter="false"
                                :display-availability-indicator="canDisplayAvailabilityIndicators"
                                :available-team-member-ids="availableTeamMemberIds"
                            />
                            <div>
                                <form-group name="sourceReference" label="Source Reference">
                                    <md-input v-model="shipmentDetails.sourceReference" />
                                </form-group>
                            </div>
                            <div>
                                <div :class="brandList.length > 0 ? 'two-column' : 'one-column'">
                                    <customer-autocomplete
                                        :label="
                                            shipmentDetails.customerId === null ? 'Customer / Contact Name' : 'Name'
                                        "
                                        id="autocustomerShipment"
                                        placeholder=""
                                        :should-focus="false"
                                        v-model="shipmentDetails"
                                        :update-customer="true"
                                        version="v2"
                                        :allow-create-customer="true"
                                        @change="handleCustomerFieldChange"
                                        @changeCustomerRateGroup="handleCustomerChangeRateGroup"
                                        @createCustomer="handleCreateCustomer"
                                        @durationMinutesAvailable="durationMinutesUpdated"
                                    />
                                    <brand-filter v-model="shipmentDetails" @brand="handleBrandListUpdated" />
                                </div>
                                <form-group name="name" label="Contact Name" v-if="shipmentDetails.customerId !== null">
                                    <md-input
                                        v-model="shipmentDetails.contact.name"
                                        data-testid="shipment-contact-name"
                                    />
                                </form-group>
                            </div>
                            <div class="stop-container">
                                <div>
                                    <google-autocomplete
                                        label="Pickup"
                                        id="pautoaddress"
                                        data-testid="pickup-address-form-field"
                                        classname="form-control autocomplete-input"
                                        placeholder=""
                                        :should-focus="false"
                                        :disable-gps-coordinates="false"
                                        gps-coordinate-placeholder="Enter a name for the pickup stop"
                                        v-model="shipmentDetails.pickupStop"
                                        name="pickupStop.address"
                                        @handleCoordinates="handlePickupCoordinates"
                                        @change="handleShipmentDetailsChanged"
                                        version="v2"
                                        @frequentAddressSelected="
                                            (info) => handleFrequentAddressSelected(info, 'pickup')
                                        "
                                    ></google-autocomplete>
                                </div>
                            </div>
                            <div class="stop-container">
                                <div>
                                    <google-autocomplete
                                        label="Drop off"
                                        id="dautoaddress"
                                        data-testid="dropoff-address-form-field"
                                        classname="form-control autocomplete-input"
                                        placeholder=""
                                        :should-focus="false"
                                        :disable-gps-coordinates="false"
                                        gps-coordinate-placeholder="Enter a name for the drop stop"
                                        @handleCoordinates="handleDropCoordinates"
                                        name="dropStop.address"
                                        v-model="shipmentDetails.dropStop"
                                        @change="handleShipmentDetailsChanged"
                                        version="v2"
                                        @frequentAddressSelected="(info) => handleFrequentAddressSelected(info, 'drop')"
                                    ></google-autocomplete>
                                </div>
                            </div>
                            <div class="contact-details">
                                <span>Shipment Contact Details</span>
                                <div class="contact-details two-column">
                                    <form-group name="phone" label="Phone">
                                        <md-input
                                            class="shipment-contact"
                                            v-model="shipmentDetails.contact.phone"
                                            data-testid="shipment-contact-phone"
                                        />
                                    </form-group>
                                    <form-group name="email" label="Email">
                                        <md-input
                                            class="shipment-contact"
                                            v-model="shipmentDetails.contact.email"
                                            data-testid="shipment-contact-email"
                                        />
                                    </form-group>
                                </div>
                            </div>
                            <form-group name="notes" label="Notes">
                                <md-textarea v-model="shipmentDetails.notes" />
                            </form-group>
                            <div v-if="shipmentCustomFieldList !== null">
                                <custom-field-inputs
                                    v-for="(customField, index) in shipmentCustomFieldList"
                                    :key="index"
                                    :custom-field-definition="customField"
                                    :initial-value="customFieldValues[customField.name]"
                                    :touched="isCustomFieldsTouched"
                                    @changed="handleCustomFieldChanged"
                                />
                            </div>
                            <div v-if="loadFieldList !== null">
                                <load-inputs
                                    v-for="(load, index) in loadFieldList"
                                    :key="index"
                                    :load-definition="load"
                                    :initial-value="loadValues"
                                    @changed="handleLoadChanged"
                                />
                            </div>
                            <form-group v-if="skillOptions.length" name="skills" label="Skills">
                                <multiselect
                                    data-testid="skills-option"
                                    v-model="shipmentDetails.skills"
                                    :options="skillOptions"
                                    :multiple="true"
                                    :close-on-select="true"
                                    placeholder="Pick required skills"
                                ></multiselect>
                            </form-group>
                            <div v-if="!isCustomerAdmin">
                                <form-group name="runnumber" label="Run (Optional)">
                                    <md-input type="number" v-model.number="shipmentDetails.runNumber" min="1" />
                                </form-group>
                            </div>
                            <div class="tripdate">
                                <md-checkbox
                                    data-testid="trip-date-checkbox"
                                    v-model="isSetTripDate"
                                    :disabled="tripDateRequired"
                                    @change="handleIsSetTripDateChange($event)"
                                >
                                    Set trip date for this shipment?
                                </md-checkbox>
                            </div>
                            <div class="shipment-datepicker" v-if="isSetTripDate" ref="bottomElement">
                                <div class="stop-container">
                                    <md-datepicker
                                        v-model="shipmentDetails.pickupStop.tripDate"
                                        md-immediately
                                        :md-debounce="10"
                                    >
                                        <label>Trip Date</label>
                                    </md-datepicker>
                                    <span
                                        v-if="
                                            isSetTripDate &&
                                                tripDateRequired &&
                                                (shipmentDetails.pickupStop.tripDate == null ||
                                                    shipmentDetails.pickupStop.tripDate == '')
                                        "
                                        class="error"
                                    >
                                        Trip date is required.
                                    </span>
                                    <trip-date-in-past
                                        :trip-date="shipmentDetails.pickupStop.tripDate"
                                        job="Shipment"
                                    />
                                </div>
                            </div>
                        </div>
                    </template>

                    <template slot="tab-pane-2">
                        <div class="contact-details">
                            <span>Pickup Details</span>
                            <customer-autocomplete
                                :label="shipmentDetails.pickupStop.customerId == null ? 'Name' : 'Customer'"
                                id="autocustomerShipmentPickup"
                                placeholder=""
                                :should-focus="false"
                                v-model="shipmentDetails.pickupStop"
                                :auto-fill-address="true"
                                version="v2"
                                :should-auto-fill-from-customer-data="shouldAutoFillFromCustomerData"
                            />
                            <form-group
                                name="name"
                                label="Contact Name"
                                v-if="shipmentDetails.pickupStop.customerId != null"
                            >
                                <md-input v-model="shipmentDetails.pickupStop.contact.name" />
                            </form-group>
                            <form-group name="phone" label="Phone">
                                <md-input v-model="shipmentDetails.pickupStop.contact.phone" />
                            </form-group>
                            <form-group name="email" label="Email">
                                <md-input v-model="shipmentDetails.pickupStop.contact.email" />
                            </form-group>
                            <form-group name="notes" label="Notes">
                                <md-textarea v-model="shipmentDetails.pickupStop.notes" />
                            </form-group>
                        </div>
                        <div class="shipment-datepicker" v-if="isSetTripDate">
                            <div class="stop-container">
                                <div class="trip-settings">
                                    <form-group name="time" label="Time" class="time-picker">
                                        <time-picker
                                            :time="pickupTime"
                                            :initial-time-window-start="shipmentDetails.pickupStop.timeWindowStart"
                                            :initial-time-window-end="shipmentDetails.pickupStop.timeWindowEnd"
                                            @selectedTime="getPickupTime"
                                        />
                                    </form-group>
                                </div>
                                <div class="trip-settings duration-setting">
                                    <form-group name="pickupStop.durationMinutes" label="Duration (mins)">
                                        <md-input type="number" v-model="shipmentDetails.pickupStop.durationMinutes" />
                                    </form-group>
                                </div>
                            </div>
                        </div>
                    </template>
                    <template slot="tab-pane-3">
                        <div class="contact-details">
                            <span>Drop off Details</span>
                            <customer-autocomplete
                                :label="shipmentDetails.dropStop.customerId == null ? 'Name' : 'Customer'"
                                id="autocustomerShipmentDrop"
                                placeholder=""
                                :should-focus="false"
                                v-model="shipmentDetails.dropStop"
                                :auto-fill-address="true"
                                version="v2"
                                :should-auto-fill-from-customer-data="shouldAutoFillFromCustomerData"
                            />
                            <form-group
                                name="name"
                                label="Contact Name"
                                v-if="shipmentDetails.dropStop.customerId != null"
                            >
                                <md-input v-model="shipmentDetails.dropStop.contact.name" />
                            </form-group>
                            <form-group name="phone" label="Phone">
                                <md-input v-model="shipmentDetails.dropStop.contact.phone" />
                            </form-group>
                            <form-group name="email" label="Email">
                                <md-input v-model="shipmentDetails.dropStop.contact.email" />
                            </form-group>
                            <form-group name="notes" label="Notes">
                                <md-textarea v-model="shipmentDetails.dropStop.notes" />
                            </form-group>
                        </div>
                        <div class="shipment-drop-datepicker" v-if="isSetTripDate">
                            <div>
                                <md-datepicker
                                    v-model="shipmentDetails.dropStop.tripDate"
                                    md-immediately
                                    :md-debounce="10"
                                >
                                    <label>Trip Date</label>
                                </md-datepicker>
                                <span
                                    v-if="
                                        isSetTripDate &&
                                            tripDateRequired &&
                                            (shipmentDetails.dropStop.tripDate == null ||
                                                shipmentDetails.dropStop.tripDate == '')
                                    "
                                    class="error"
                                >
                                    Trip date is required.
                                </span>
                            </div>
                            <div>
                                <form-group name="time" label="Time" class="time-picker">
                                    <time-picker
                                        :time="dropTime"
                                        :initial-time-window-start="shipmentDetails.dropStop.timeWindowStart"
                                        :initial-time-window-end="shipmentDetails.dropStop.timeWindowEnd"
                                        @selectedTime="getDropTime"
                                    />
                                </form-group>
                            </div>
                            <div>
                                <form-group name="dropStop.durationMinutes" label="Duration (mins)">
                                    <md-input type="number" v-model="shipmentDetails.dropStop.durationMinutes" />
                                </form-group>
                            </div>
                        </div>
                    </template>
                </tabs>
            </form-wrapper>
        </div>
        <div class="modal-footer">
            <md-button class="dialog-button md-primary" :disabled="ratesIsLoading" @click="createShipment">
                Create
            </md-button>
            <md-button class="dialog-button md-default" @click.stop="$modal.hide">Cancel</md-button>
        </div>
    </div>
</template>

<script>
import marked from 'marked';
import { handleRequests, isAValidCoordinate, showErrorMessage } from '@/helpers';
import { requiredIf, numeric, maxLength } from 'vuelidate/lib/validators';
import { STOP_DETAILS_DEFAULTS } from '@/utils/defaults';
import { MapOverviewMixin } from '@/mixins/MapOverviewMixin';
import _ from 'lodash';

import {
    TimePicker,
    GoogleAutocomplete,
    Tabs,
    CustomFieldInputs,
    CustomerAutocomplete,
    LoadInputs,
    TeamRegionMemberControls,
    ChargingTypeOptions,
    ChargeListModal,
    BrandFilter,
    TripDateInPast
} from '@/components';
import moment from 'moment';
import { BrandsMixin, GeneralMixin, RatesEngineMixin, CustomFieldValidationMixin } from '@/mixins';
import { RosteringMixin } from '@/mixins/RosteringMixin';
import {
    DATE_TYPES,
    CREATE_SHIPMENT_REQUIRED_BOOKING_FIELDS,
    CREATE_SHIPMENT_PICKUP_REQUIRED_BOOKING_FIELDS,
    CREATE_SHIPMENT_DROPOFF_REQUIRED_BOOKING_FIELDS
} from '@/utils/constants';
import { ReverseBitwise } from '@/utils/ApplyRatesToUtils';
import { mapGetters } from 'vuex';

export default {
    name: 'CreateShipmentModal',
    components: {
        TimePicker,
        GoogleAutocomplete,
        Tabs,
        CustomFieldInputs,
        CustomerAutocomplete,
        LoadInputs,
        TeamRegionMemberControls,
        ChargingTypeOptions,
        ChargeListModal,
        BrandFilter,
        TripDateInPast
    },
    mixins: [GeneralMixin, MapOverviewMixin, RatesEngineMixin, BrandsMixin, RosteringMixin, CustomFieldValidationMixin],
    props: {
        shipmentDetails: {
            type: Object,
            default: () => {}
        },
        members: {
            type: Array,
            default: () => []
        },
        resolve: {
            type: Function,
            default: () => {}
        },
        createCustomer: {
            type: Function,
            default: () => {}
        },
        displayAvailabilityIndicator: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        ...mapGetters({
            user: 'user/user',
            isCustomerAdmin: 'user/isCustomerAdmin',
            isCustomer: 'user/isCustomer',
            isSingleUser: 'user/isIndividualUser',
            isSingleTeamMember: 'team/isSingleTeamMember',
            isSingleTeamMemberUser: 'user/isSingleTeamMember',
            isSupportUser: 'user/isSupportUser',
            allWarehouses: 'team/warehouses',
            hasRateRules: 'user/hasRateRules'
        }),
        /**
         * Contact info, notes, etc. can be auto-filled from customer data
         * But we only want this to happen if it's not a customer user
         */
        shouldAutoFillFromCustomerData() {
            return !this.isCustomer && !this.isCustomerAdmin;
        },
        canDisplayAvailabilityIndicators() {
            return this.$_canDisplayAvailabilityIndicators(
                this.displayAvailabilityIndicator,
                this.shipmentDetails.tripDate
            );
        }
    },
    async mounted() {
        if (
            // eslint-disable-next-line eqeqeq
            (this.shipmentDetails.assignToPublicUserId !== null && this.shipmentDetails.assignToPublicUserId !== 0) ||
            this.isSingleTeamMemberUser
        ) {
            this.isSetTripDate = true;
            this.tripDateRequired = true;
        }

        this.shipmentCustomFieldList = this.user.shipmentCustomFieldDefinitions;
        this.loadFieldList = this.user.vehicleCapacityUnitsConfiguration || [];
        if (this.user.skillOptions) {
            this.skillOptions = this.user.skillOptions.map((x) => x.name);
        }

        if ((this.isSingleUser || this.isSingleTeamMember) && !this.isCustomerAdmin && !this.isSupportUser) {
            this.shipmentDetails.assignToPublicUserId = this.user.publicUserId;
        }

        this.retrieveRequiredFlags();
        this.initializeCustomFields();
        this.initializeLoads();
        if (this.canDisplayAvailabilityIndicators) {
            await this.$_getAvailableTeamMembers(this.shipmentDetails.tripDate);
        }
    },
    watch: {
        isSetTripDate(newValue, oldValue) {
            // eslint-disable-next-line eqeqeq
            if (newValue) {
                this.scrollToBottom();
                this.shipmentDetails.tripDate = moment().format(DATE_TYPES.internationalDate);
                this.shipmentDetails.pickupStop.tripDate = moment().format(DATE_TYPES.internationalDate);
                this.shipmentDetails.dropStop.tripDate = moment().format(DATE_TYPES.internationalDate);
            } else {
                this.shipmentDetails.tripDate = null;
                this.shipmentDetails.pickupStop.appointmentTime = null;
                this.shipmentDetails.pickupStop.tripDate = null;
                this.shipmentDetails.dropStop.appointmentTime = null;
                this.shipmentDetails.dropStop.tripDate = null;
            }
        },
        'shipmentDetails.assignToPublicUserId': function(newValue, oldValue) {
            // eslint-disable-next-line eqeqeq
            if (newValue !== null && newValue != 0) {
                this.isSetTripDate = true;
                this.tripDateRequired = true;
                this.setDurationMinutes(newValue);
            } else {
                this.tripDateRequired = false;
            }
        },
        'shipmentDetails.pickupStop.address': function() {
            this.frequentAddressSelected = false;
        },
        'shipmentDetails.dropStop.address': function() {
            this.frequentAddressSelected = false;
        },
        'shipmentDetails.pickupStop.tripDate': function(newValue, oldValue) {
            // eslint-disable-next-line eqeqeq
            this.shipmentDetails.tripDate = newValue;
            if (newValue) {
                this.shipmentDetails.pickupStop.tripDate = newValue;
                if (oldValue === this.shipmentDetails.dropStop.tripDate) {
                    this.shipmentDetails.dropStop.tripDate = newValue;
                }
            }
        },
        'shipmentDetails.tripDate': async function(newValue, oldValue) {
            // eslint-disable-next-line eqeqeq
            if (newValue !== oldValue) {
                this.validateCanGenerateRates();

                if (this.canDisplayAvailabilityIndicators) {
                    await this.$_getAvailableTeamMembers(this.shipmentDetails.tripDate);
                }
            }
        },
        'shipmentDetails.customerId': function(newValue) {
            this.shipmentDetails.pickupStop.customerId = newValue;
            this.shipmentDetails.dropStop.customerId = newValue;
        }
    },
    methods: {
        handleBrandListUpdated(brandList) {
            this.brandList = brandList;
        },
        handleCreateCustomer() {
            this.createCustomer({
                ...this.shipmentDetails,
                load: this.loadValues,
                customFields: this.customFieldValues
            });
        },
        /**
         *
         * @param {import("@/jsDocTypes/FrequentAddress").FrequentAddress} info
         * @param {string} type - Valid values are 'pickup' or 'drop'
         */
        handleFrequentAddressSelected(info, type) {
            this.frequentAddressSelected = true;
            /* eslint-disable */
            let {
                email,
                contactName,
                phone,
                notes,
                timeWindowStart,
                timeWindowEnd,
                customFields,
                durationMinutes,
                customerId
            } = info;
            if (timeWindowStart || timeWindowEnd) {
                this.isSetTripDate = true;
                if (timeWindowStart) {
                    const time = moment.utc(timeWindowStart).format('HH:mm');
                    timeWindowStart = time.toString();
                }
                if (timeWindowEnd) {
                    const time = moment.utc(timeWindowEnd).format('HH:mm');
                    timeWindowEnd = time.toString();
                }
            }
            if (type === 'pickup') {
                this.shipmentDetails.pickupStop = {
                    ...this.shipmentDetails.pickupStop,
                    contact: {
                        email,
                        name: contactName,
                        phone
                    },
                    notes,
                    timeWindowStart,
                    timeWindowEnd,
                    durationMinutes
                };
                if (!this.shipmentDetails.pickupStop.customerId && !!customerId) {
                    this.shipmentDetails.pickupStop.customerId = customerId;
                }
                if (!durationMinutes) {
                    if (durationMinutes == 0 && this.customerDurationMinutes > 0) {
                        this.shipmentDetails.pickupStop.durationMinutes = this.customerDurationMinutes;
                    } else {
                        this.isFrequentAddressPickupSelected = true;
                    }
                }
            } else {
                this.shipmentDetails.dropStop = {
                    ...this.shipmentDetails.dropStop,
                    contact: {
                        email,
                        name: contactName,
                        phone
                    },
                    notes,
                    timeWindowStart,
                    timeWindowEnd,
                    durationMinutes
                };
                if (!this.shipmentDetails.dropStop.customerId && !!customerId) {
                    this.shipmentDetails.dropStop.customerId = customerId;
                }
                if (!durationMinutes) {
                    if (durationMinutes == 0 && this.customerDurationMinutes > 0) {
                        this.shipmentDetails.dropStop.durationMinutes = this.customerDurationMinutes;
                    } else {
                        this.isFrequentAddressDropoffSelected = true;
                    }
                }
            }
            if (!this.shipmentDetails.customerId && !!customerId) {
                this.shipmentDetails.customerId = customerId;
            }
            if (customFields) {
                this.customFieldValues = JSON.parse(customFields);
            }
        },
        initializeCustomFields() {
            if (
                this.shipmentCustomFieldList &&
                this.shipmentCustomFieldList.length > 0 &&
                !this.shipmentDetails.customFields
            ) {
                const custFieldValueList = {};
                this.shipmentCustomFieldList.forEach((item, index) => {
                    if (item.type.toLowerCase() !== 'checkbox') {
                        custFieldValueList[item.name] = null;
                    } else {
                        custFieldValueList[item.name] = false;
                    }
                });
                this.customFieldValues = custFieldValueList;
            } else {
                this.customFieldValues = this.shipmentDetails.customFields;
            }
        },
        initializeLoads() {
            if (this.loadFieldList && this.loadFieldList.length > 0 && !this.shipmentDetails.load) {
                const loadList = {};
                this.loadFieldList.forEach((item, index) => {
                    loadList[item.type] = null;
                });
                this.loadValues = loadList;
            } else {
                this.loadValues = this.shipmentDetails.load;
            }
        },
        handleSwitchTab(val) {
            if (val.toLowerCase() === 'shipment') {
                // logic to avoid unintended update in some details around stop addresses when going back
                // to 'Shipment' tab from 'Pickup Options' or 'Drop Options' tab
                this.hasUpdateFreeze = true;
            }
        },
        disableUpdateFreeze() {
            if (this.hasUpdateFreeze) {
                this.hasUpdateFreeze = false;
            }
        },
        async createShipment() {
            this.shipmentDetails.load = this.loadValues;
            if (
                !this.validateRequiredFields(
                    this.shipmentDetails,
                    CREATE_SHIPMENT_REQUIRED_BOOKING_FIELDS,
                    this.shipmentFieldsBitwiseArray,
                    'Shipment Tab'
                ) |
                    !this.validateRequiredFields(
                        this.shipmentDetails.dropStop,
                        CREATE_SHIPMENT_DROPOFF_REQUIRED_BOOKING_FIELDS,
                        this.shipmentDropoffFieldsBitwiseArray,
                        'Drop Off Tab'
                    ) |
                    !this.validateRequiredFields(
                        this.shipmentDetails.pickupStop,
                        CREATE_SHIPMENT_PICKUP_REQUIRED_BOOKING_FIELDS,
                        this.shipmentPickupFieldsBitwiseArray,
                        'Pickup Tab'
                    ) ||
                !this.validateRequiredCustomFields()
            ) {
                return;
            }

            if (
                (this.isSetTripDate && this.tripDateRequired && this.shipmentDetails.pickupStop.tripDate == null) ||
                this.isPickupTimeValid === false ||
                this.isDropTimeValid === false
            )
                return;

            const { assignToPublicUserId } = this.shipmentDetails;
            this.$v.shipmentDetails.$touch();
            this.isFormTouched = true;

            if (!this.$v.shipmentDetails.$invalid) {
                this.$_handleLoaderState(true);
                // eslint-disable-next-line eqeqeq
                if (assignToPublicUserId === null || assignToPublicUserId === 0)
                    this.shipmentDetails.assignToPublicUserId = null;

                // eslint-disable-next-line radix
                this.shipmentDetails.pickupStop.durationMinutes = parseInt(
                    this.shipmentDetails.pickupStop.durationMinutes,
                    10
                );
                // eslint-disable-next-line radix
                this.shipmentDetails.dropStop.durationMinutes = parseInt(
                    this.shipmentDetails.dropStop.durationMinutes,
                    10
                );
                this.shipmentDetails.customFields = JSON.stringify(this.customFieldValues);

                if (this.shipmentDetails.pickupStop.tripDate) {
                    this.shipmentDetails.tripDate = moment(this.shipmentDetails.pickupStop.tripDate).format(
                        DATE_TYPES.internationalDate
                    );
                    this.shipmentDetails.pickupStop.tripDate = moment(this.shipmentDetails.pickupStop.tripDate).format(
                        DATE_TYPES.internationalDate
                    );
                }

                if (this.shipmentDetails.dropStop.tripDate) {
                    this.shipmentDetails.tripDate = null;
                    this.shipmentDetails.dropStop.tripDate = moment(this.shipmentDetails.dropStop.tripDate).format(
                        DATE_TYPES.internationalDate
                    );
                }

                if (!this.isSetTripDate) {
                    this.shipmentDetails.tripDate = null;
                    this.shipmentDetails.pickupStop.appointmentTime = null;
                    this.shipmentDetails.pickupStop.timeWindowStart = null;
                    this.shipmentDetails.pickupStop.timeWindowEnd = null;
                    this.shipmentDetails.pickupStop.tripDate = null;
                    this.shipmentDetails.dropStop.appointmentTime = null;
                    this.shipmentDetails.dropStop.timeWindowStart = null;
                    this.shipmentDetails.dropStop.timeWindowEnd = null;
                    this.shipmentDetails.dropStop.tripDate = null;
                }
                /* this.shipmentDetails.pickupStop.runNumber = this.shipmentDetails.runNumber;
                this.shipmentDetails.dropStop.runNumber = this.shipmentDetails.runNumber; */

                this.shipmentDetails.chargeLines = this.chargeList;
                this.shipmentDetails.costLines = this.costList;
                this.shipmentDetails.shipmentAppliedChargingRule = this.chargeRule;
                this.shipmentDetails.shipmentAppliedCostingRule = this.costRule;

                const api = '/api/shipments';
                const payload = {
                    method: 'post',
                    data: this.shipmentDetails
                };

                try {
                    const responseData = await handleRequests(api, payload);
                    this.$notify({
                        message: 'Successfully created a shipment.',
                        type: 'success'
                    });

                    if (responseData) {
                        const { shipmentId, shipmentRef, feedbackMessage } = responseData?.data;
                        if (
                            feedbackMessage &&
                            typeof feedbackMessage === 'string' &&
                            feedbackMessage.trim().length !== 0
                        ) {
                            this.$notifyWarning(feedbackMessage, 7000);
                        }

                        this.showToast(shipmentId, shipmentRef);
                    }

                    this.$v.$reset();
                    this.resolve('ok');
                } catch (e) {
                    let message = 'Could not create a new shipment.';
                    if (e && e.data) [{ message }] = e.data;
                    this.$notify({
                        message,
                        type: 'danger'
                    });
                    this.$_handleLoaderState(false);
                }
            }
        },
        getPickupTime(selectedTime, isTimeValid) {
            if (selectedTime !== null) {
                const { appointmentTime, timeWindowStart, timeWindowEnd } = selectedTime;
                this.shipmentDetails.pickupStop.appointmentTime = appointmentTime;
                this.shipmentDetails.pickupStop.timeWindowStart = timeWindowStart;
                this.shipmentDetails.pickupStop.timeWindowEnd = timeWindowEnd;
                this.pickupTime = this.$_getTime(appointmentTime, timeWindowStart, timeWindowEnd);
            } else {
                this.shipmentDetails.pickupStop.appointmentTime = null;
                this.shipmentDetails.pickupStop.timeWindowStart = null;
                this.shipmentDetails.pickupStop.timeWindowEnd = null;
                this.pickupTime = null;
            }

            this.isPickupTimeValid = isTimeValid;
        },
        getDropTime(selectedTime, isTimeValid) {
            if (selectedTime != null) {
                const { appointmentTime, timeWindowStart, timeWindowEnd } = selectedTime;
                this.shipmentDetails.dropStop.appointmentTime = appointmentTime;
                this.shipmentDetails.dropStop.timeWindowStart = timeWindowStart;
                this.shipmentDetails.dropStop.timeWindowEnd = timeWindowEnd;
                this.dropTime = this.$_getTime(appointmentTime, timeWindowStart, timeWindowEnd);
            } else {
                this.shipmentDetails.dropStop.appointmentTime = null;
                this.shipmentDetails.dropStop.timeWindowStart = null;
                this.shipmentDetails.dropStop.timeWindowEnd = null;
                this.dropTime = null;
            }

            this.isDropTimeValid = isTimeValid;
        },
        resetShipmentState() {
            Object.assign(this.shipmentDetails, STOP_DETAILS_DEFAULTS());
            this.$v.$reset();
        },
        handleCustomFieldChanged({ name, value }) {
            this.customFieldValues[name] = value;
        },
        handleLoadChanged({ name, value }) {
            this.loadValues[name] = parseFloat(value);
            this.ruleGenerationLoadDetails();
        },
        handleCustomerFieldChange(text) {
            this.ruleGenerationLoadDetails();
        },
        async handleTeamRegionMemberChanged(val) {
            if (val.teamMember.rateGroupId !== null && this.shipmentDetails.rateGroupId === null)
                this.shipmentDetails.rateGroupId = val.teamMember.rateGroupId;

            this.shipmentDetails.teamRegionId = val.teamRegionId;
            this.shipmentDetails.pickupStop.teamRegionId = val.teamRegionId;
            this.shipmentDetails.dropStop.teamRegionId = val.teamRegionId;
            this.shipmentDetails.assignToPublicUserId = val.publicUserId;

            await this.getDefaultWarehouse(val.teamRegionId ?? val.teamMember?.teamRegionId ?? -1);
            this.validateCanGenerateRates();

            this.disableUpdateFreeze();
        },
        handleBrandChange(value) {
            this.shipmentDetails.brandId = value;
        },
        setDurationMinutes(publicUserId) {
            const teamMember = this.members.find((x) => x.publicUserId === publicUserId);
            const defaultStopDurationMinutes =
                teamMember?.defaultStopDurationMinutes ?? this.user.defaultStopDurationMinutes;
            // User user or team default stop duration, if null use value from STOP_DETAILS_DEFAULTS
            this.shipmentDetails.pickupStop.durationMinutes =
                defaultStopDurationMinutes ?? STOP_DETAILS_DEFAULTS().durationMinutes;
            this.shipmentDetails.dropStop.durationMinutes =
                defaultStopDurationMinutes ?? STOP_DETAILS_DEFAULTS().durationMinutes;
        },
        handlePickupCoordinates(value) {
            this.usePickupCoordinates = value;
            this.$v.$reset();
        },
        handleDropCoordinates(value) {
            this.useDropCoordinates = value;
            this.$v.$reset();
        },
        async getDefaultWarehouse(teamRegionId) {
            if (!this.user.publicTeamId) return;
            if (!this.allWarehouses.length) return;
            const teamRegionIdToUse = teamRegionId === -1 ? null : teamRegionId;
            const defaultPickup = this.allWarehouses.find(
                (x) => x.isDefaultShipmentPickupStop && x.teamRegionId === teamRegionIdToUse
            );
            this.populateStopWithWarehouse(this.shipmentDetails.pickupStop, defaultPickup);

            const defaultDrop = this.allWarehouses.find(
                (x) => x.isDefaultShipmentDropStop && x.teamRegionId === teamRegionIdToUse
            );
            this.populateStopWithWarehouse(this.shipmentDetails.dropStop, defaultDrop);
        },
        // eslint-disable-next-line func-names
        handleShipmentDetailsChanged: _.debounce(function() {
            if (
                this.shipmentDetails.pickupStop.address !== this.pickupAddress ||
                this.shipmentDetails.dropStop.address !== this.dropStop
            ) {
                this.validateCanGenerateRates(true);

                this.pickupAddress = this.shipmentDetails.pickupStop.address;
                this.dropStop = this.shipmentDetails.dropStop.address;
            }
        }, 500),
        async generateRates(addressChanged, rateGroupChanged) {
            if (!this.hasRateRules) return;

            if (!this.shipmentDetails.rateGroupId) {
                this.chargeList = [];
                this.costList = [];

                this.shipmentDetails.chargeLines = this.chargeList;
                this.shipmentDetails.costLines = this.costList;
                return;
            }

            this.ratesIsLoading = true;

            let response = {
                data: {}
            };

            try {
                const data = this.$_getshipmentDataModel(
                    this.shipmentDetails,
                    this.user,
                    this.loadValues,
                    null,
                    null,
                    true,
                    this.shipmentDetails.assignToPublicUserId !== null &&
                        this.shipmentDetails.assignToPublicUserId !== 0,
                    false,
                    false,
                    rateGroupChanged,
                    addressChanged
                );

                const payload = {
                    method: 'post',
                    data
                };

                response = await handleRequests('/api/rates/shipment-rates', payload);

                if (response.data) {
                    this.rateListData = response.data;
                    this.chargeList = response.data.charge ?? [];
                    this.costList = response.data.cost ?? [];
                    this.currency = this.rateListData?.currency;
                    this.chargeRule = response.data.chargeRule;
                    this.costrule = response.data.costRule;

                    this.shipmentDetails.distance = response.data.distance ?? 0;
                }

                this.ratesIsLoading = false;
            } catch (error) {
                const message = `Error generating rates, if issue persists please contact support`;
                showErrorMessage(this, message, null);
                this.ratesIsLoading = false;
            }
        },
        openCharges() {
            document.querySelector('.charge-wrapper').classList.toggle('side-panel-open');
            document.querySelector('.charge-wrapper').classList.toggle('side-panel');
        },
        handleRateTypeChanged(rateRule) {
            this.shipmentDetails.rateGroupId = rateRule?.rateGroupId ?? null;
            this.validateCanGenerateRates(false, true);
        },
        validateCanGenerateRates(addressChanged, rateGroupChanged) {
            if (
                this.shipmentDetails.pickupStop.address !== null &&
                this.shipmentDetails.dropStop.address !== null &&
                this.ratesIsLoading === false
            ) {
                this.generateRates(addressChanged, rateGroupChanged);
            }
        },
        // eslint-disable-next-line func-names
        ruleGenerationDebounce: _.debounce(function() {
            this.validateCanGenerateRates();
        }, 500),
        // eslint-disable-next-line func-names
        ruleGenerationLoadDetails: _.debounce(function() {
            this.validateCanGenerateRates();
        }, 1000),
        populateStopWithWarehouse(stop, warehouseDetails) {
            if (!warehouseDetails) {
                if (this.hasUpdateFreeze) return;
            } else {
                stop.location = {
                    latitude: warehouseDetails.location.latitude,
                    longitude: warehouseDetails.location.longitude
                };
                stop.name = warehouseDetails.name;
                stop.address = warehouseDetails.address;
                stop.coordinateAddress = warehouseDetails.coordinateAddress;
                stop.addressComponents = { ...warehouseDetails.addressComponents };
                stop.contact.name = `${warehouseDetails.firstName || ''} ${warehouseDetails.lastName || ''}`.trim();
                stop.contact.phone = warehouseDetails.phone;
                stop.contact.email = warehouseDetails.email;
                stop.notes = warehouseDetails.defaultStopNotes;
            }
        },
        handleCustomerChangeRateGroup(customerRateGroupId) {
            if (this.shipmentDetails.rateGroupId === null || this.shipmentDetails.rateGroupId === undefined) {
                this.shipmentDetails.rateGroupId = customerRateGroupId;
            }
        },
        durationMinutesUpdated(durationMinutes) {
            this.customerDurationMinutes = durationMinutes;
            if (this.customerDurationMinutes > 0) {
                if (!this.isFrequentAddressPickupSelected) {
                    this.shipmentDetails.pickupStop.durationMinutes = this.customerDurationMinutes;
                }
                if (!this.isFrequentAddressDropoffSelected) {
                    this.shipmentDetails.dropStop.durationMinutes = this.customerDurationMinutes;
                }
            }
        },
        scrollToBottom() {
            if (this.manualIsSetTripDateToggle) {
                this.$nextTick(() => {
                    this.$refs.bottomElement.scrollIntoView({ behavior: 'smooth' });
                });

                this.manualIsSetTripDateToggle = false;
            }
        },
        handleIsSetTripDateChange(val) {
            this.manualIsSetTripDateToggle = val;
        },
        async retrieveRequiredFlags() {
            this.$_handleLoaderState(true);
            const api = `/api/booking-fields-flags`;
            try {
                const response = await handleRequests(api, { method: 'get' });
                this.requiredBookingFieldsFlags = response.data;
                this.shipmentFieldsBitwiseArray = ReverseBitwise(
                    this.requiredBookingFieldsFlags.requiredShipmentFieldsFlags,
                    CREATE_SHIPMENT_REQUIRED_BOOKING_FIELDS
                );
                this.shipmentPickupFieldsBitwiseArray = ReverseBitwise(
                    this.requiredBookingFieldsFlags.requiredShipmentPickupFieldsFlags,
                    CREATE_SHIPMENT_PICKUP_REQUIRED_BOOKING_FIELDS
                );
                this.shipmentDropoffFieldsBitwiseArray = ReverseBitwise(
                    this.requiredBookingFieldsFlags.requiredShipmentDropoffFieldsFlags,
                    CREATE_SHIPMENT_DROPOFF_REQUIRED_BOOKING_FIELDS
                );
                this.$_handleLoaderState(false);
            } catch (error) {
                const message = 'Error in retrieving booking fields flags';
                showErrorMessage(this, message, error);
                this.$_handleLoaderState(false);
            }
        },
        validateRequiredFields(details, fields, bitwiseArray, tab) {
            let isValid = true;
            details.load = this.loadValues;
            bitwiseArray.forEach((bitwiseElement) => {
                const field = fields.find((x) => x.key === bitwiseElement);
                // early return to reduce nested if-else
                if (!field) {
                    return;
                }
                const referenceArray = field.ref.split('.');

                // for nested objects like contact.name
                if (referenceArray.length > 1) {
                    if (
                        referenceArray[0] in details &&
                        referenceArray[1] in details[referenceArray[0]] &&
                        !details[referenceArray[0]][referenceArray[1]]
                    ) {
                        isValid = false;
                        const message = `Please fill in the required field: ${field.label} in the ${tab}`;
                        showErrorMessage(this, message, null);
                        return;
                    }
                    if (this.loadFieldList.some((el) => el.type === referenceArray[1])) {
                        // Checks the load list if load is not null, early return if true
                        if (this.loadValues[referenceArray[1]]) {
                            return;
                        }
                        isValid = false;
                        const message = `Please fill in the required field: ${field.label} in the ${tab}`;
                        showErrorMessage(this, message, null);
                    }
                }

                // for single level objects like address
                const [referenceElement] = referenceArray;
                // checks if the element exists in the details object and if it's falsy
                if (!details[referenceElement]) {
                    // special case: if it's the 'appointmentTime', we check if either appointmentTime or timeWindowStart and timeWindowEnd are filled
                    // if true, early return - meaning the field is valid
                    if (
                        referenceElement === 'appointmentTime' &&
                        (details.appointmentTime || (details.timeWindowStart && details.timeWindowEnd))
                    ) {
                        return;
                    }
                    isValid = false;
                    const message = `Please fill in the required field: ${field.label} in the ${tab}`;
                    showErrorMessage(this, message, null);
                }
            });
            return isValid;
        },
        validateRequiredCustomFields() {
            return this.$_validateRequiredCustomFields(this.shipmentCustomFieldList, this.customFieldValues);
        },
        showToast(shipmentId, shipmentRef) {
            const endPoint = '/shipments/details';
            const notifyMessage = marked(
                `You can find the created shipment by clicking the following link: <a onmouseout="this.style.color='white'" style="color:white !important; text-decoration: underline;" target="_blank" href="${endPoint}/${shipmentId}">${shipmentRef}</a>`
            );

            this.$notifySuccess(notifyMessage, 10000);
        }
    },
    data() {
        return {
            disabledDates: (date) => {
                const x = moment(date).format(DATE_TYPES.internationalDate);
                const now = moment().format(DATE_TYPES.internationalDate);
                return !moment(x).isSameOrAfter(now, 'day');
            },
            isSetTripDate: false,
            tripDateRequired: false,
            isPickupTimeValid: true,
            isDropTimeValid: true,
            shipmentCustomFieldList: [],
            customFieldValues: {},
            loadFieldList: [],
            loadValues: {},
            skillOptions: [],
            teamMemberOptions: [],
            pickupTime: 'None',
            dropTime: 'None',
            additionalPickupTimeOption: [],
            additionalDropTimeOption: [],
            isFormTouched: false,
            usePickupCoordinates: false,
            useDropCoordinates: false,
            rateListData: null,
            currency: '',
            chargeList: [],
            ratesIsLoading: false,
            pickupAddress: null,
            dropAddress: null,
            costList: [],
            manualIsSetTripDateToggle: false,
            hasUpdateFreeze: false,
            /**
             * Used to monitor when the latest data change came from a frequent address.
             * This is used to avoid unintended update in some details around stop addresses when going back
             */
            frequentAddressSelected: false,
            isFrequentAddressPickupSelected: false,
            isFrequentAddressDropoffSelected: false,
            customerDurationMinutes: 0,
            brandList: [],
            requiredBookingFieldsFlags: {
                requiredStopFieldsFlags: 0,
                requiredShipmentFieldsFlags: 0,
                requiredShipmentPickupFieldsFlags: 0,
                requiredShipmentDropoffFieldsFlags: 0
            },
            chargeRule: null,
            costRule: null
        };
    },
    validations: {
        shipmentDetails: {
            pickupStop: {
                address: {
                    required:
                        // eslint-disable-next-line func-names
                        requiredIf(function() {
                            return !this.usePickupCoordinates;
                        }),
                    coordinatesValid(data) {
                        if (this.usePickupCoordinates) {
                            return isAValidCoordinate(data);
                        }
                        return true;
                    }
                },
                durationMinutes: {
                    required:
                        // eslint-disable-next-line func-names
                        requiredIf(function() {
                            return this.isSetTripDate;
                        }),
                    numeric
                }
            },
            dropStop: {
                address: {
                    required:
                        // eslint-disable-next-line func-names
                        requiredIf(function() {
                            return !this.useDropCoordinates;
                        }),
                    coordinatesValid(data) {
                        if (this.useDropCoordinates) {
                            return isAValidCoordinate(data);
                        }
                        return true;
                    }
                },
                durationMinutes: {
                    required:
                        // eslint-disable-next-line func-names
                        requiredIf(function() {
                            return this.isSetTripDate;
                        }),
                    numeric
                }
            },
            sourceReference: {
                maxLength: maxLength(200)
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.modal-container {
    max-width: 800px;

    .modal-body {
        min-height: 400px;
        padding-bottom: 0;

        ::v-deep .md-card-tabs .tab-content {
            max-height: 65vh;
            overflow: auto;
        }
    }

    .stats {
        flex-grow: 8;

        .md-icon {
            color: #ffd900;
        }
    }

    .tip {
        font-size: 12px;
    }
}

.custom-tab-list {
    margin-top: -20px;

    ::v-deep .md-card-header {
        padding: 0;
    }

    ::v-deep .md-card-content {
        padding: 0;

        .md-list {
            margin: 0;

            .md-list-item:first-child {
                margin-left: 0;
            }
        }
    }
}

.shipment-form-container {
    padding-top: 20px;
    max-height: 80vh;
    padding-right: 10px;
}

.trip-settings {
    float: left;
    margin-right: 20px;
    margin-top: 20px;
    min-width: 125px;
    max-width: 30%;
}

.duration-setting {
    min-width: 90px;
    max-width: 100px;
}

.contact-details {
    margin-top: 20px;
}

::v-deep .md-menu-content.md-select-menu {
    width: 150px;
}

.shipment-drop-datepicker {
    display: flex;
    flex-wrap: wrap;
    margin-top: 10px;

    ::v-deep .md-clear {
        display: none;
    }

    > div {
        margin-right: 20px;
        min-width: 125px;
    }

    > div:first-child {
        width: 35%;
    }

    > div:last-child {
        margin-right: 0;
        min-width: 100px;
        max-width: 100px;
    }

    .error {
        font-size: 12px;
        color: red;
        margin-top: -10px;
        display: block;
    }

    .md-input {
        width: 100%;
    }

    .time-picker:after {
        height: 0;
    }

    .time-window {
        width: 100%;

        > div {
            width: 100%;
        }

        > div:first-child {
            text-transform: uppercase;
            display: block;
            font-size: 12px;
            font-weight: 600;
            text-align: left;
        }

        > div:nth-child(2) {
            ::v-deep .md-radio {
                margin: 0;
                margin-right: 15px;
            }

            text-transform: uppercase;
            text-align: left;
            font-size: 12px;
        }
    }
}

.charge-wrapper {
    padding-left: 25px;
}

.chargeButton-toggle {
    float: right;
    border: none;
    background-color: #4caf50;
    position: relative;
    place-items: center;
    display: grid;
    color: white !important;
}

.chargeButton-toggle:hover {
    background: #409443;
}

.sp-icon-close {
    display: none !important;
}

.sp-icon-open {
}

.material-symbols-outlined {
    place-items: center;
    display: grid;
    color: #ffff;
}

.two-column {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 10px;
}

.one-column {
    display: grid;
    grid-template-columns: 1fr;
}
</style>
